import { FormattedMessage } from 'react-intl';
import { concat, EMPTY, from, merge, of } from 'rxjs';
import { catchError, mergeMap, takeWhile } from 'rxjs/operators';

import { NotificationService, NOTIFICATION_DURATIONS, NOTIFICATION_TYPE_ERROR } from 'ui-components/services/notification-service';

import { BULK_APPLY_RATE_RECOMMENDATIONS } from '../../services/pusher-service/pusher-contract-messages';
import { bulkApply } from '../../fulfillment-planner/rate-recommendation/rate-recommendations.api';
import { bulkloadFulfillmentPlans } from '../bulkload/bulkload.actions';
import { createPusherMessageCache } from '../pusher/message-cache';
import Global from '../global-selectors';
import { messages } from '../bulk/purchase/messages';
import { orderGridGetRatesRequest } from '../orders/grid/orders.grid.header.actions';

export const bulkApplyRecommendations = (getState, request, onSuccess, onError) => {
  const { findMessage } = createPusherMessageCache([BULK_APPLY_RATE_RECOMMENDATIONS]);

  const sellerDefaultCurrency = Global.selectSellerDefaultCurrency(getState());

  return from(bulkApply(request)).pipe(
    mergeMap((bulkApplyRecommendationsResponse) => {
      const applyRecommendationsJobGuid = bulkApplyRecommendationsResponse.data.jobGuids[0];
      const messageChecker = (pusherMessage) => pusherMessage.messageGuid === applyRecommendationsJobGuid;
      const getIsJobComplete = (pusherMessage) => pusherMessage?.data?.final;

      const updatedFulfillmentPlanIds = [];
      const failedFulfillmentPlanIds = [];
      const amountSaved = {
        code: '',
        value: 0,
      };

      return findMessage(messageChecker).pipe(
        takeWhile((pusherMessage) => !getIsJobComplete(pusherMessage), true),
        mergeMap((pusherMessage) => {
          const isJobComplete = getIsJobComplete(pusherMessage);

          if (!isJobComplete) {
            const {
              FulfillmentPlanId: fulfillmentPlanId,
              IsSuccessful: isSuccessful,
              TotalSavings: totalSavings,
            } = pusherMessage?.data?.result || {};

            amountSaved.value += totalSavings?.value || 0;
            amountSaved.code = totalSavings?.code || sellerDefaultCurrency;

            if (isSuccessful) {
              updatedFulfillmentPlanIds.push(fulfillmentPlanId);
            } else {
              failedFulfillmentPlanIds.push(fulfillmentPlanId);
            }

            return EMPTY;
          }

          if (failedFulfillmentPlanIds.length > 0) {
            return onError();
          }

          return onSuccess({ amountSaved, updatedFulfillmentPlanIds });
        }),
      );
    }),
    catchError(onError),
  );
};

export const reloadUpdatedFulfillmentPlans = (updatedFulfillmentPlanIds) => {
  return merge(of(bulkloadFulfillmentPlans(updatedFulfillmentPlanIds)), of(orderGridGetRatesRequest(updatedFulfillmentPlanIds)));
};

export const bulkApplyRecommendationsFromLabelPurchaseCostReview = (getState, request, onSuccess, onError) => {
  const handleSuccess = ({ amountSaved, updatedFulfillmentPlanIds }) => {
    return concat(onSuccess(amountSaved), reloadUpdatedFulfillmentPlans(updatedFulfillmentPlanIds));
  };

  const handleError = () => {
    NotificationService.showNotification({
      notificationType: NOTIFICATION_TYPE_ERROR,
      title: <FormattedMessage {...messages.applyRecommendationsErrorTitle} />,
      content: <FormattedMessage {...messages.applyRecommendationsErrorContent} />,
      duration: NOTIFICATION_DURATIONS.MEDIUM,
    });

    return onError();
  };

  return bulkApplyRecommendations(getState, request, handleSuccess, handleError);
};
